import { Howl, Howler } from "howler";
import { tokens } from "@/art/tokens";
import { clamp } from "@/utils/clamp";

/**
 * 오디오 허브. BGM 한 트랙 + SFX 풀 + 마스터 게인.
 *
 * 브라우저 자동재생 정책 때문에 **사용자 첫 인터랙션 전에는 재생 시도 금지**.
 * Home 씬의 "여정 시작" 버튼 같은 곳에서 최초 1회 `unlock()` 을 호출해 깨운다.
 */
class AudioManagerImpl {
    private bgm: Howl | null = null;
    private currentBgmSrc: string | null = null;
    private sfxCache = new Map<string, Howl>();
    private uiNoiseBuffer: AudioBuffer | null = null;
    /**
     * 코어 UI SFX 는 한동안 Web Audio 절차합성으로 대체되어 왔다.
     * 이제는 실제 에셋 퀄리티를 올려서 쓰는 방향이므로 기본값은 false.
     * 필요하면 디버깅/비교 청취용으로만 다시 켤 수 있다.
     */
    private preferProceduralCoreSfx = false;

    private masterGain: number = tokens.audio.defaults.master;
    private bgmGain: number = tokens.audio.defaults.bgm;
    private sfxGain: number = tokens.audio.defaults.sfx;
    private muted = false;
    private unlocked = false;

    unlock(): void {
        if (this.unlocked) return;
        // 아무 Howl 이나 하나 재생해 두면 Web Audio context 가 unlock 됨.
        // 이미 ctx.resume 해주는 howler 에 맡긴다.
        Howler.volume(this.masterGain);
        this.unlocked = true;
    }

    async crossfadeTo(src: string | undefined, durationMs: number = tokens.timing.bgmCrossfade): Promise<void> {
        if (!src) {
            if (this.bgm) await this.fadeOutAndStop(this.bgm, durationMs);
            this.bgm = null;
            this.currentBgmSrc = null;
            return;
        }
        if (src === this.currentBgmSrc && this.bgm) return;

        // BGM 은 html5:true (streaming) — 큰 mp3 를 Web Audio 로 decode 하느라 수 초 걸리는 걸 피함.
        // load 완료를 기다리지 않고 fire-and-forget 으로 재생 시작 — 브라우저가 스트리밍으로 이어받음.
        const next = new Howl({ src: [src], loop: true, volume: 0, html5: true });
        next.once("loaderror", (_id, err) => {
            console.warn(`[AudioManager] BGM load failed: ${src}`, err);
        });

        next.play();
        next.fade(0, this.muted ? 0 : this.bgmGain * this.masterGain, durationMs);

        if (this.bgm) {
            const old = this.bgm;
            old.fade(old.volume(), 0, durationMs);
            window.setTimeout(() => old.stop(), durationMs + 200);
        }
        this.bgm = next;
        this.currentBgmSrc = src;
    }

    private async fadeOutAndStop(h: Howl, durationMs: number): Promise<void> {
        h.fade(h.volume(), 0, durationMs);
        await new Promise<void>(r => setTimeout(r, durationMs + 100));
        h.stop();
    }

    private get audioContext(): AudioContext | null {
        const runtime = Howler as typeof Howler & { ctx?: AudioContext };
        return runtime.ctx ?? null;
    }

    private get masterNode(): GainNode | null {
        const runtime = Howler as typeof Howler & { masterGain?: GainNode };
        return runtime.masterGain ?? null;
    }

    private createUiNoiseBuffer(ctx: AudioContext): AudioBuffer {
        if (this.uiNoiseBuffer) return this.uiNoiseBuffer;

        const duration = 0.12;
        const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate);
        const data = buffer.getChannelData(0);

        for (let i = 0; i < data.length; i += 1) {
            const t = i / data.length;
            const white = Math.random() * 2 - 1;
            const shimmer = Math.sin(t * Math.PI * 18) * 0.18;
            data[i] = (white * 0.82 + shimmer) * Math.pow(1 - t, 3.8);
        }

        this.uiNoiseBuffer = buffer;
        return buffer;
    }

    private playUiClick(kind: "tap" | "confirm", gainOverride?: number): boolean {
        const ctx = this.audioContext;
        const master = this.masterNode;
        if (!ctx || !master) return false;

        void ctx.resume().catch(() => {});

        const now = ctx.currentTime + 0.002;
        const baseGain = (gainOverride ?? 1) * this.sfxGain;

        const output = ctx.createGain();
        output.gain.setValueAtTime(baseGain, now);

        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(-18, now);
        compressor.knee.setValueAtTime(14, now);
        compressor.ratio.setValueAtTime(2.5, now);
        compressor.attack.setValueAtTime(0.003, now);
        compressor.release.setValueAtTime(0.09, now);

        const lowpass = ctx.createBiquadFilter();
        lowpass.type = "lowpass";
        lowpass.frequency.setValueAtTime(kind === "tap" ? 6800 : 5400, now);
        lowpass.Q.setValueAtTime(0.4, now);

        output.connect(compressor);
        compressor.connect(lowpass);
        lowpass.connect(master);

        const transient = ctx.createBufferSource();
        transient.buffer = this.createUiNoiseBuffer(ctx);

        const transientFilter = ctx.createBiquadFilter();
        transientFilter.type = "bandpass";
        transientFilter.frequency.setValueAtTime(kind === "tap" ? 3400 : 2600, now);
        transientFilter.Q.setValueAtTime(kind === "tap" ? 0.9 : 0.7, now);

        const transientGain = ctx.createGain();
        transientGain.gain.setValueAtTime(0.0001, now);
        transientGain.gain.exponentialRampToValueAtTime(kind === "tap" ? 0.05 : 0.038, now + 0.0025);
        transientGain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === "tap" ? 0.018 : 0.026));

        transient.connect(transientFilter);
        transientFilter.connect(transientGain);
        transientGain.connect(output);

        const bodyOsc = ctx.createOscillator();
        bodyOsc.type = "triangle";
        bodyOsc.frequency.setValueAtTime(kind === "tap" ? 1240 : 920, now);
        bodyOsc.frequency.exponentialRampToValueAtTime(kind === "tap" ? 760 : 620, now + (kind === "tap" ? 0.05 : 0.08));

        const bodyFilter = ctx.createBiquadFilter();
        bodyFilter.type = "lowpass";
        bodyFilter.frequency.setValueAtTime(kind === "tap" ? 2600 : 2200, now);
        bodyFilter.Q.setValueAtTime(0.7, now);

        const bodyGain = ctx.createGain();
        bodyGain.gain.setValueAtTime(0.0001, now);
        bodyGain.gain.exponentialRampToValueAtTime(kind === "tap" ? 0.055 : 0.045, now + 0.004);
        bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === "tap" ? 0.06 : 0.1));

        bodyOsc.connect(bodyFilter);
        bodyFilter.connect(bodyGain);
        bodyGain.connect(output);

        const glassOsc = ctx.createOscillator();
        glassOsc.type = "sine";
        glassOsc.frequency.setValueAtTime(kind === "tap" ? 2200 : 1680, now);
        glassOsc.frequency.exponentialRampToValueAtTime(kind === "tap" ? 1420 : 1260, now + (kind === "tap" ? 0.045 : 0.09));

        const glassGain = ctx.createGain();
        glassGain.gain.setValueAtTime(0.0001, now);
        glassGain.gain.exponentialRampToValueAtTime(kind === "tap" ? 0.03 : 0.024, now + 0.003);
        glassGain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === "tap" ? 0.05 : 0.085));

        const slapback = ctx.createDelay();
        slapback.delayTime.setValueAtTime(kind === "tap" ? 0.026 : 0.04, now);

        const slapGain = ctx.createGain();
        slapGain.gain.setValueAtTime(kind === "tap" ? 0.18 : 0.22, now);

        glassOsc.connect(glassGain);
        glassGain.connect(output);
        glassGain.connect(slapback);
        slapback.connect(slapGain);
        slapGain.connect(output);

        if (kind === "confirm") {
            const sparkle = ctx.createOscillator();
            sparkle.type = "sine";
            sparkle.frequency.setValueAtTime(1880, now + 0.016);
            sparkle.frequency.exponentialRampToValueAtTime(1420, now + 0.13);

            const sparkleGain = ctx.createGain();
            sparkleGain.gain.setValueAtTime(0.0001, now + 0.012);
            sparkleGain.gain.exponentialRampToValueAtTime(0.026, now + 0.03);
            sparkleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);

            sparkle.connect(sparkleGain);
            sparkleGain.connect(output);
            sparkle.start(now + 0.012);
            sparkle.stop(now + 0.15);
        }

        transient.start(now);
        transient.stop(now + 0.12);
        bodyOsc.start(now);
        bodyOsc.stop(now + 0.13);
        glassOsc.start(now);
        glassOsc.stop(now + 0.14);

        return true;
    }

    private playCardSelect(gainOverride?: number): boolean {
        const ctx = this.audioContext;
        const master = this.masterNode;
        if (!ctx || !master) return false;

        void ctx.resume().catch(() => {});

        const now = ctx.currentTime + 0.002;
        const baseGain = (gainOverride ?? 1) * this.sfxGain;

        const output = ctx.createGain();
        output.gain.setValueAtTime(baseGain, now);

        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(-22, now);
        compressor.knee.setValueAtTime(18, now);
        compressor.ratio.setValueAtTime(2.2, now);
        compressor.attack.setValueAtTime(0.004, now);
        compressor.release.setValueAtTime(0.12, now);

        output.connect(compressor);
        compressor.connect(master);

        const whoosh = ctx.createBufferSource();
        whoosh.buffer = this.createUiNoiseBuffer(ctx);

        const whooshFilter = ctx.createBiquadFilter();
        whooshFilter.type = "bandpass";
        whooshFilter.frequency.setValueAtTime(1450, now);
        whooshFilter.frequency.exponentialRampToValueAtTime(900, now + 0.11);
        whooshFilter.Q.setValueAtTime(0.55, now);

        const whooshGain = ctx.createGain();
        whooshGain.gain.setValueAtTime(0.0001, now);
        whooshGain.gain.exponentialRampToValueAtTime(0.035, now + 0.012);
        whooshGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);

        whoosh.connect(whooshFilter);
        whooshFilter.connect(whooshGain);
        whooshGain.connect(output);

        const body = ctx.createOscillator();
        body.type = "triangle";
        body.frequency.setValueAtTime(780, now);
        body.frequency.exponentialRampToValueAtTime(520, now + 0.12);

        const bodyFilter = ctx.createBiquadFilter();
        bodyFilter.type = "lowpass";
        bodyFilter.frequency.setValueAtTime(1800, now);
        bodyFilter.Q.setValueAtTime(0.8, now);

        const bodyGain = ctx.createGain();
        bodyGain.gain.setValueAtTime(0.0001, now);
        bodyGain.gain.exponentialRampToValueAtTime(0.05, now + 0.008);
        bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.135);

        body.connect(bodyFilter);
        bodyFilter.connect(bodyGain);
        bodyGain.connect(output);

        const sheen = ctx.createOscillator();
        sheen.type = "sine";
        sheen.frequency.setValueAtTime(1760, now + 0.018);
        sheen.frequency.exponentialRampToValueAtTime(1240, now + 0.12);

        const sheenGain = ctx.createGain();
        sheenGain.gain.setValueAtTime(0.0001, now + 0.012);
        sheenGain.gain.exponentialRampToValueAtTime(0.02, now + 0.03);
        sheenGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);

        const shimmerDelay = ctx.createDelay();
        shimmerDelay.delayTime.setValueAtTime(0.038, now);

        const shimmerGain = ctx.createGain();
        shimmerGain.gain.setValueAtTime(0.2, now);

        sheen.connect(sheenGain);
        sheenGain.connect(output);
        sheenGain.connect(shimmerDelay);
        shimmerDelay.connect(shimmerGain);
        shimmerGain.connect(output);

        whoosh.start(now);
        whoosh.stop(now + 0.13);
        body.start(now);
        body.stop(now + 0.15);
        sheen.start(now + 0.012);
        sheen.stop(now + 0.14);

        return true;
    }

    private playPageTurn(gainOverride?: number): boolean {
        const ctx = this.audioContext;
        const master = this.masterNode;
        if (!ctx || !master) return false;

        void ctx.resume().catch(() => {});

        const now = ctx.currentTime + 0.002;
        const baseGain = (gainOverride ?? 1) * this.sfxGain * 0.9;

        const output = ctx.createGain();
        output.gain.setValueAtTime(baseGain, now);

        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(-24, now);
        compressor.knee.setValueAtTime(16, now);
        compressor.ratio.setValueAtTime(2, now);
        compressor.attack.setValueAtTime(0.006, now);
        compressor.release.setValueAtTime(0.18, now);

        const lowpass = ctx.createBiquadFilter();
        lowpass.type = "lowpass";
        lowpass.frequency.setValueAtTime(4200, now);
        lowpass.Q.setValueAtTime(0.35, now);

        output.connect(compressor);
        compressor.connect(lowpass);
        lowpass.connect(master);

        const pageBrush = ctx.createBufferSource();
        pageBrush.buffer = this.createUiNoiseBuffer(ctx);

        const brushFilter = ctx.createBiquadFilter();
        brushFilter.type = "bandpass";
        brushFilter.frequency.setValueAtTime(820, now);
        brushFilter.frequency.exponentialRampToValueAtTime(1800, now + 0.11);
        brushFilter.Q.setValueAtTime(0.45, now);

        const brushGain = ctx.createGain();
        brushGain.gain.setValueAtTime(0.0001, now);
        brushGain.gain.exponentialRampToValueAtTime(0.028, now + 0.012);
        brushGain.gain.exponentialRampToValueAtTime(0.009, now + 0.045);
        brushGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);

        pageBrush.connect(brushFilter);
        brushFilter.connect(brushGain);
        brushGain.connect(output);

        const body = ctx.createOscillator();
        body.type = "triangle";
        body.frequency.setValueAtTime(520, now);
        body.frequency.exponentialRampToValueAtTime(320, now + 0.13);

        const bodyGain = ctx.createGain();
        bodyGain.gain.setValueAtTime(0.0001, now);
        bodyGain.gain.exponentialRampToValueAtTime(0.018, now + 0.01);
        bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);

        body.connect(bodyGain);
        bodyGain.connect(output);

        const chime = ctx.createOscillator();
        chime.type = "sine";
        chime.frequency.setValueAtTime(1480, now + 0.028);
        chime.frequency.exponentialRampToValueAtTime(1180, now + 0.16);

        const chimeGain = ctx.createGain();
        chimeGain.gain.setValueAtTime(0.0001, now + 0.02);
        chimeGain.gain.exponentialRampToValueAtTime(0.014, now + 0.05);
        chimeGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.17);

        const tailDelay = ctx.createDelay();
        tailDelay.delayTime.setValueAtTime(0.042, now);

        const tailGain = ctx.createGain();
        tailGain.gain.setValueAtTime(0.16, now);

        chime.connect(chimeGain);
        chimeGain.connect(output);
        chimeGain.connect(tailDelay);
        tailDelay.connect(tailGain);
        tailGain.connect(output);

        pageBrush.start(now);
        pageBrush.stop(now + 0.14);
        body.start(now);
        body.stop(now + 0.14);
        chime.start(now + 0.02);
        chime.stop(now + 0.18);

        return true;
    }

    sfx(src: string, gainOverride?: number): void {
        if (this.muted) return;

        if (this.preferProceduralCoreSfx) {
            if (src.endsWith("/ui-tap.mp3") && this.playUiClick("tap", gainOverride)) return;
            if (src.endsWith("/ui-confirm.mp3") && this.playUiClick("confirm", gainOverride)) return;
            if (src.endsWith("/card-flip.mp3") && this.playCardSelect(gainOverride)) return;
            if (src.endsWith("/page-turn.mp3") && this.playPageTurn(gainOverride)) return;
        }

        let h = this.sfxCache.get(src);
        if (!h) {
            h = new Howl({
                src: [src],
                volume: (gainOverride ?? 1) * this.sfxGain * this.masterGain,
            });
            this.sfxCache.set(src, h);
        } else {
            h.volume((gainOverride ?? 1) * this.sfxGain * this.masterGain);
        }
        h.play();
    }

    preloadSfx(srcs: string[]): Promise<void[]> {
        return Promise.all(srcs.map(src => new Promise<void>((resolve) => {
            if (this.sfxCache.has(src)) return resolve();
            let settled = false;
            const finish = () => { if (!settled) { settled = true; resolve(); } };
            const h = new Howl({ src: [src], preload: true, volume: this.sfxGain * this.masterGain });
            this.sfxCache.set(src, h);
            h.once("load", finish);
            h.once("loaderror", finish); // 실패해도 앱 막지 않음
            window.setTimeout(finish, 3000); // 이벤트 미발화 안전망
        })));
    }

    setMaster(v: number): void {
        this.masterGain = clamp(v, 0, 1);
        Howler.volume(this.masterGain);
        if (this.bgm) this.bgm.volume(this.muted ? 0 : this.bgmGain * this.masterGain);
    }

    setBgmGain(v: number): void {
        this.bgmGain = clamp(v, 0, 1);
        if (this.bgm) this.bgm.volume(this.muted ? 0 : this.bgmGain * this.masterGain);
    }

    setSfxGain(v: number): void {
        this.sfxGain = clamp(v, 0, 1);
    }

    setMuted(v: boolean): void {
        this.muted = v;
        Howler.mute(v);
    }

    get state() {
        return {
            master: this.masterGain,
            bgm: this.bgmGain,
            sfx: this.sfxGain,
            muted: this.muted,
            currentBgm: this.currentBgmSrc,
            unlocked: this.unlocked,
        };
    }
}

export const AudioManager = new AudioManagerImpl();
